import Lodash from 'lodash';
const components = [
    'protocol',
    'hostname',
    'port',
    'pathname',
    'search',
    'hash',
];
const defaultPorts = {
    'http:': '80',
    'https:': '443',
    'ftp:': '21',
    'sftp:': '22',
};
export function isValidURL(str) {
    return /^(((ftp|sftp|http|https):)?\/\/)?[a-zа-я0-9]+([-.]{1}[a-zа-я0-9]+)*\.[a-zа-я]{2,10}(:[0-9]{1,5})?(\/.*)?$/i.test(str);
}
export const ERRORS = {
    invalidURL: '[URL] Could not parse url',
    invalidPort: '[URL] Port should be a convertible to Number value',
    invalidParamName: '[URL] Invalid parameter name',
};
export const parseUrl = (() => {
    if (global.URL) {
        return (url) => {
            return Lodash.pick(new global.URL(url, document.location), components);
        };
    }
    const aElement = document.createElement('a');
    return (url) => {
        aElement.href = url;
        return Lodash.pick(aElement, components);
    };
})();
export class URL {
    constructor(url, options = {}) {
        this._map = {}; // will be filled in constructor
        const { skipValidation = false } = options;
        if (!skipValidation && !isValidURL(url)) {
            throw new Error(ERRORS.invalidURL);
        }
        const parts = parseUrl(url);
        Lodash.forEach(parts, (value, key) => {
            this[key] = value;
        });
    }
    get protocol() {
        return this._map.protocol;
    }
    set protocol(val) {
        if (Lodash.isNil(val) || val === '') {
            this._map.protocol = '';
        }
        else {
            this._map.protocol = Lodash.includes(val, ':') ? val : `${val}:`;
        }
    }
    get domain() {
        return this._map.hostname;
    }
    set domain(val) {
        if (Lodash.isNil(val) || val === '') {
            this._map.hostname = '';
        }
        else if (Lodash.isArray(val)) {
            this._map.hostname = val.join('.');
        }
        else {
            this._map.hostname = `${val}`;
        }
    }
    get hostname() {
        return this.domain;
    }
    set hostname(val) {
        this.domain = val;
    }
    get port() {
        const { port } = this._map;
        const protocol = this.protocol;
        if (port === defaultPorts[protocol]) {
            return '';
        }
        return port;
    }
    set port(val) {
        if (Lodash.isNil(val) || val === '') {
            this._map.port = '';
        }
        else {
            if (!/^\d+$/.test(val)) {
                throw new Error(ERRORS.invalidPort);
            }
            this._map.port = String(val);
        }
    }
    get path() {
        return this._map.pathname;
    }
    set path(val) {
        if (Lodash.isNil(val) || val === '') {
            this._map.pathname = '';
        }
        else if (Lodash.isArray(val)) {
            this._map.pathname = `/${val.join('/')}`;
        }
        else {
            this._map.pathname = Lodash.startsWith(val, '/')
                ? String(val)
                : `/${val}`;
        }
    }
    get pathname() {
        return this.path;
    }
    set pathname(val) {
        this.path = val;
    }
    get search() {
        return this._map.search;
    }
    set search(val) {
        if (Lodash.isNil(val) || val === '') {
            this._map.search = '';
        }
        else if (Lodash.isString(val) && val.length) {
            this._map.search = Lodash.includes(val, '?') ? val : `?${val}`;
        }
        else if (Lodash.isNumber(val)) {
            this._map.search = `?${val}`;
        }
        else {
            const keys = Lodash.keys(val);
            if (keys.length) {
                this._map.search = `?${Lodash.map(keys, (key) => `${encodeURIComponent(key)}=${encodeURIComponent(val[key])}`).join('&')}`;
            }
            else {
                this._map.search = '';
            }
        }
    }
    get hash() {
        return this._map.hash;
    }
    set hash(val) {
        if (Lodash.isNil(val) || val === '') {
            this._map.hash = '';
        }
        else {
            this._map.hash = Lodash.includes(val, '#') ? val : `#${val}`;
        }
    }
    get filename() {
        return this.path.split('/').pop();
    }
    set filename(val) {
        const pathArray = this.path.split('/').slice(1, -1);
        if (!(Lodash.isNil(val) || val === '')) {
            pathArray.push(val);
        }
        this.path = pathArray;
    }
    addParam(name, value = '') {
        if (!name) {
            throw new Error(ERRORS.invalidParamName);
        }
        const params = this._parseSearch(this.search);
        params[name] = value;
        this.search = params;
    }
    removeParam(name) {
        if (!name) {
            throw new Error(ERRORS.invalidParamName);
        }
        const params = this._parseSearch(this.search);
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete params[name];
        this.search = params;
    }
    hasParam(name) {
        return !Lodash.isNil(this.getParam(name));
    }
    getParam(name) {
        const params = this._parseSearch(this.search);
        return params[name];
    }
    _parseSearch(searchString) {
        const params = {};
        searchString = searchString.replace('?', '');
        if (!searchString.length) {
            return params;
        }
        const parts = searchString.split('&');
        Lodash.forEach(parts, (part) => {
            const [name, value = ''] = part.split('=');
            params[decodeURIComponent(name)] = decodeURIComponent(value);
        });
        return params;
    }
    get href() {
        const port = this.port ? `:${this.port}` : '';
        return `${this.protocol}//${this.domain}${port}${this.path}${this.search}${this.hash}`;
    }
    toString() {
        return this.href;
    }
}
export * from './build-url';
export * from './get-origin';
//# sourceMappingURL=index.js.map